// The newest readings of a set of feeds, for their cards.
//
// Shared by the dashboard and the group page. Each preview resolves on its
// own, so one slow or failing feed is one card's problem, and a feed that has
// never received a sample is waiting rather than broken.

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { listReadings } from '@/api/endpoints';
import { queryKeys } from '@/query/keys';
import { useSession } from '@/session/SessionProvider';
import { buildHistory, type ChartKind, type HistoryPoint } from '@/features/readings/history';

/**
 * Points drawn per feed card. Enough to show a shape and the newest value,
 * small enough that a page of a dozen cards stays within a sensible request
 * budget.
 */
export const PREVIEW_LIMIT = 30;

/** The state of one preview request. */
export type PreviewState =
  | { status: 'pending' }
  | { status: 'unsupported' }
  | { status: 'error' }
  | {
      status: 'ready';
      points: HistoryPoint[];
      /** A number, or the newest boolean state of a boolean channel. */
      latest: number | boolean | null;
      latestAt: string | null;
    };

/** A feed to preview, keyed by whatever its page uses to find the card. */
export interface PreviewTarget {
  key: string;
  datastreamId: string | null;
  chartKind: ChartKind | null;
}

/** Load each target's newest readings; the result is keyed by `key`. */
export function useFeedPreviews(targets: PreviewTarget[]): Map<string, PreviewState> {
  const { accountId } = useSession();
  const account = accountId ?? 'anonymous';
  const loaded = useMemo(
    () => targets.filter((target) => target.datastreamId !== null && target.chartKind !== null),
    [targets],
  );
  const queries = useQueries({
    queries: loaded.map((target) => ({
      queryKey: queryKeys.feedPreview(account, target.datastreamId as string),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        listReadings(target.datastreamId as string, { limit: PREVIEW_LIMIT, signal }),
      enabled: accountId !== null,
    })),
  });

  return useMemo(() => {
    const result = new Map<string, PreviewState>();
    for (const target of targets) {
      if (target.datastreamId === null) {
        // A channel without a datastream has simply never received a sample;
        // that is a normal state, not a failure to report as an error.
        result.set(target.key, { status: 'ready', points: [], latest: null, latestAt: null });
      } else if (target.chartKind === null) {
        result.set(target.key, { status: 'unsupported' });
      }
    }
    loaded.forEach((target, index) => {
      const query = queries[index];
      if (!query || query.isPending) {
        result.set(target.key, { status: 'pending' });
        return;
      }
      if (query.isError) {
        result.set(target.key, { status: 'error' });
        return;
      }
      const history = buildHistory(query.data?.items ?? [], target.chartKind as ChartKind);
      result.set(target.key, {
        status: 'ready',
        points: history.points,
        latest: history.latest,
        latestAt: history.latestAt,
      });
    });
    return result;
  }, [targets, loaded, queries]);
}
